import { streamEvent } from '@frisk/shared';
import type { CalendarFeed, FeedRole, Category, Item, Member, Notes, Settings, ShoppingItem, Stroke, CalendarEvent, ExpiryView, ShoppingRule, WeatherPlace, WeatherStatus } from '@frisk/shared';

export type ItemWithExpiry = Item & { expiry: ExpiryView };

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

/** Thrown for a write attempted with no connection — see OfflineBanner. */
export class OfflineError extends ApiError {
  constructor() {
    super('Keine Verbindung — Änderung nicht gespeichert', 0);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON body when there actually is one. Sending
  // `Content-Type: application/json` on a bodyless DELETE makes Fastify reject
  // it with "Body cannot be empty when content-type is set to
  // 'application/json'" — which silently broke every delete in the app.
  const sendsJson = init?.body !== undefined && !(init.body instanceof FormData);

  // A write with no network would otherwise surface as a bare TypeError and a
  // vague toast. Reads are allowed through: the service worker may still
  // answer them from cache.
  const isWrite = Boolean(init?.method) && init!.method !== 'GET';
  if (isWrite && typeof navigator !== 'undefined' && !navigator.onLine) throw new OfflineError();

  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: 'include', // the household cookie
    headers: sendsJson ? { 'Content-Type': 'application/json', ...init?.headers } : init?.headers,
  });

  if (res.status === 204) return undefined as T;
  const body = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
  if (!res.ok) throw new ApiError(body?.message ?? res.statusText, res.status);
  return body as T;
}

export const api = {
  authState: () =>
    request<{ needsSetup: boolean; authenticated: boolean; setupTokenRequired: boolean }>('/auth/state'),
  register: (body: { username: string; password: string; setupToken?: string }) =>
    request<{ username: string }>('/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  login: (body: { username: string; password: string }) =>
    request<{ username: string }>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => request<{ username: string }>('/auth/me'),

  listSessions: () =>
    request<Array<{ id: string; userAgent: string | null; lastSeenAt: string | null; createdAt: string; current: boolean }>>(
      '/auth/sessions',
    ),
  revokeSession: (id: string) => request<void>(`/auth/sessions/${id}`, { method: 'DELETE' }),
  changePassword: (current: string, next: string) =>
    request<{ ok: true }>('/auth/password', { method: 'POST', body: JSON.stringify({ current, next }) }),

  listItems: () => request<ItemWithExpiry[]>('/items'),
  createItem: (body: {
    name: string;
    bestBefore?: string | null;
    qty?: number;
    barcode?: string | null;
    imageUrl?: string | null;
  }) =>
    request<ItemWithExpiry>('/items', { method: 'POST', body: JSON.stringify(body) }),
  patchItem: (id: string, body: Record<string, unknown>) =>
    request<ItemWithExpiry>(`/items/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  consumeItem: (id: string, qty = 1) =>
    request<ItemWithExpiry>(`/items/${id}/consume`, { method: 'POST', body: JSON.stringify({ qty }) }),
  removeItem: (id: string) => request<void>(`/items/${id}`, { method: 'DELETE' }),

  listShopping: () => request<ShoppingItem[]>('/shopping'),
  addShopping: (name: string) =>
    request<ShoppingItem>('/shopping', { method: 'POST', body: JSON.stringify({ name }) }),
  toggleShopping: (id: string, done: boolean) =>
    request<ShoppingItem>(`/shopping/${id}`, { method: 'PATCH', body: JSON.stringify({ done }) }),
  clearDoneShopping: () => request<{ ok: true }>('/shopping/clear-done', { method: 'POST' }),

  listRules: () => request<ShoppingRule[]>('/shopping-rules'),
  createRule: (body: { label: string; barcode?: string | null; minQty: number }) =>
    request<ShoppingRule>('/shopping-rules', { method: 'POST', body: JSON.stringify(body) }),
  patchRule: (id: string, body: { minQty?: number; enabled?: boolean }) =>
    request<ShoppingRule>(`/shopping-rules/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeRule: (id: string) => request<void>(`/shopping-rules/${id}`, { method: 'DELETE' }),

  listMembers: () => request<Member[]>('/members'),
  listEvents: (from: string, to: string) =>
    request<CalendarEvent[]>(`/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),

  lookupBarcode: (barcode: string) =>
    request<{ barcode: string; name: string; brand: string | null; imageUrl: string | null; defaultShelfDays: number | null }>(
      `/products/${barcode}`,
    ),
  /** "Manuell anlegen" — the panel remembers this barcode from now on. */
  teachProduct: (barcode: string, name: string) =>
    request<{ barcode: string; name: string }>(`/products/${barcode}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    }),


  createMember: (name: string) =>
    request<Member>('/members', { method: 'POST', body: JSON.stringify({ name }) }),
  renameMember: (id: string, name: string) =>
    request<Member>(`/members/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  removeMember: (id: string) => request<void>(`/members/${id}`, { method: 'DELETE' }),

  getSettings: () => request<Settings>('/settings'),
  patchSettings: (patch: Partial<Settings>) =>
    request<Settings>('/settings', { method: 'PATCH', body: JSON.stringify(patch) }),

  listCategories: () => request<Category[]>('/categories'),

  listFeeds: () => request<CalendarFeed[]>('/calendar/feeds'),
  createFeed: (body: { label: string; url: string; role: FeedRole; memberId: string | null }) =>
    request<CalendarFeed & { sync: { count: number; error: string | null } }>('/calendar/feeds', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchFeed: (id: string, body: { label?: string; role?: FeedRole; memberId?: string | null; enabled?: boolean }) =>
    request<CalendarFeed>(`/calendar/feeds/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  importFeed: (body: { label: string; role: FeedRole; filename?: string; ics: string }) =>
    request<CalendarFeed & { sync: { count: number; error: string | null } }>('/calendar/feeds/import', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reimportFeed: (id: string, ics: string) =>
    request<{ sync: { count: number; error: string | null } }>(`/calendar/feeds/${id}/import`, {
      method: 'POST',
      body: JSON.stringify({ ics }),
    }),
  removeFeed: (id: string) => request<void>(`/calendar/feeds/${id}`, { method: 'DELETE' }),
  syncFeed: (id: string) =>
    request<{ count: number; error: string | null }>(`/calendar/feeds/${id}/sync`, { method: 'POST' }),
  patchCategory: (id: string, defaultShelfDays: number) =>
    request<Category>(`/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ defaultShelfDays }),
    }),

  createScanLink: () =>
    request<{ token: string; claimWindowSeconds: number }>('/scan/link', { method: 'POST' }),
  scanLinkState: (token: string) =>
    request<{ claimed: boolean; revoked: boolean; expired: boolean; itemCount: number }>(
      `/scan/link/${token}`,
    ),
  revokeScanLink: (token: string) => request<void>(`/scan/link/${token}`, { method: 'DELETE' }),

  bringStatus: () =>
    request<{
      available: boolean; connected: boolean; listName: string | null;
      itemCount: number; lastSyncedAt: string | null; lastError: string | null;
    }>('/integrations/bring/status'),
  bringLists: (email: string, password: string) =>
    request<{ lists: Array<{ listUuid: string; name: string }>; defaultListUuid: string }>(
      '/integrations/bring/lists',
      { method: 'POST', body: JSON.stringify({ email, password }) },
    ),
  bringConnect: (body: {
    email: string; password: string; listUuid: string; listName: string;
    migrateExisting: boolean; rememberPassword: boolean;
  }) => request<{ itemCount: number }>('/integrations/bring/connect', { method: 'POST', body: JSON.stringify(body) }),
  bringSync: () => request<{ count: number; error: string | null }>('/integrations/bring/sync', { method: 'POST' }),
  bringDisconnect: () => request<void>('/integrations/bring', { method: 'DELETE' }),

  weather: () => request<WeatherStatus>('/integrations/weather'),
  weatherSearch: (q: string) =>
    request<{ places: WeatherPlace[] }>(`/integrations/weather/search?q=${encodeURIComponent(q)}`),
  weatherSetPlace: (place: WeatherPlace) =>
    request<WeatherStatus>('/integrations/weather', { method: 'PUT', body: JSON.stringify({ place }) }),
  weatherDisconnect: () => request<void>('/integrations/weather', { method: 'DELETE' }),

  getNotes: () => request<Notes>('/notes'),
  putNotes: (strokes: Stroke[]) =>
    request<Notes>('/notes', { method: 'PUT', body: JSON.stringify({ strokes }) }),

  sttHealth: () => request<{ available: boolean; reason?: string }>('/stt/health'),

  transcribe: (audio: Blob) => {
    const form = new FormData();
    form.append('file', audio, 'utterance.wav');
    return request<{ text: string; date: string | null; rule: string | null }>('/stt', {
      method: 'POST',
      body: form,
    });
  },
};

/**
 * Server-sent events. Auth rides the cookie because EventSource cannot set
 * headers — that constraint is why the session is a cookie and not a bearer.
 */
export function subscribe(
  onEvent: (name: string, payload?: unknown) => void,
  onReconnect?: () => void,
): () => void {
  const source = new EventSource('/api/stream', { withCredentials: true });
  // Derived from the shared enum rather than restated here: a hand-kept copy
  // silently drops new event types — the server broadcasts them and nothing
  // listens, which looks like a broken feature rather than a missing line.
  // 'tick' is the keep-alive and means nothing changed.
  for (const name of streamEvent.options.filter((n) => n !== 'tick')) {
    source.addEventListener(name, (e) => {
      // Payloads are optional: most events say only "something changed", but
      // a phone scan carries what it put away so the panel can name it.
      let payload: unknown;
      try {
        const raw = (e as MessageEvent).data;
        if (raw) payload = JSON.parse(raw);
      } catch {
        /* an event without a body is normal */
      }
      onEvent(name, payload);
    });
  }
  // EventSource retries on its own, but anything that happened while the
  // connection was down was simply missed — so a reconnect has to trigger a
  // refetch, not just resume listening.
  let wasOpen = false;
  source.addEventListener('open', () => {
    if (wasOpen) onReconnect?.();
    wasOpen = true;
  });
  return () => source.close();
}
